/**
 * Egyptian phone number utilities
 * Makes phone input more forgiving for Egyptian users
 */

/**
 * Normalize Egyptian phone number to international format (+20XXXXXXXXX)
 * Accepts various formats and converts them to +20XXXXXXXXX
 */
export const normalizeEgyptianPhoneNumber = (
  phoneNumber: string
): {
  success: boolean;
  phoneNumber?: string;
  error?: string;
} => {
  if (!phoneNumber || typeof phoneNumber !== "string") {
    return {
      success: false,
      error: "رقم الهاتف مطلوب",
    };
  }
  // Remove all spaces, dashes, and parentheses
  let cleaned = phoneNumber.replace(/[\s\-()]/g, "");

  // Remove any leading zeros
  cleaned = cleaned.replace(/^0+/, "");

  // Handle different Egyptian phone number formats
  if (cleaned.startsWith("+20")) {
    // Already in international format (+20XXXXXXXXX)
    // No change needed
  } else if (cleaned.startsWith("20")) {
    // Missing + prefix (20XXXXXXXXX)
    cleaned = "+" + cleaned;
  } else if (cleaned.startsWith("0")) {
    // Local format with leading zero (0XXXXXXXXX)
    cleaned = "+20" + cleaned.substring(1);
  } else if (cleaned.length === 10 && /^[1-9]/.test(cleaned)) {
    // Local format without leading zero (1XXXXXXXXX)
    cleaned = "+20" + cleaned;
  } else if (cleaned.length === 11 && cleaned.startsWith("01")) {
    // Local format with 01 prefix (01XXXXXXXXX)
    cleaned = "+20" + cleaned.substring(1);
  } else {
    // Try to add +20 prefix if it looks like it could be an Egyptian number
    if (cleaned.length >= 9 && cleaned.length <= 11 && /^\d+$/.test(cleaned)) {
      cleaned = "+20" + cleaned;
    } else {
      return {
        success: false,
        error: "تنسيق رقم الهاتف غير صحيح. استخدم تنسيق مثل: 01234567890",
      };
    }
  }

  // Validate final format
  // Egyptian mobile numbers: +201XXXXXXXXX (11 digits after +20)
  const phoneRegex = /^\+20(1[0-9]{9})$/;

  if (!phoneRegex.test(cleaned)) {
    return {
      success: false,
      error: "رقم الهاتف غير صحيح. تأكد من أنه رقم مصري صالح",
    };
  }

  return {
    success: true,
    phoneNumber: cleaned,
  };
};

/**
 * Format Egyptian phone number for display
 * Converts +201234567890 to a more readable format
 */
export const formatEgyptianPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber.startsWith("+20")) {
    return phoneNumber;
  }

  const digits = phoneNumber.substring(3); // Remove +20

  if (digits.length === 10 && digits.startsWith("1")) {
    // Mobile number: +20 1XX XXX XXXX
    return `+20 ${digits.substring(0, 3)} ${digits.substring(
      3,
      6
    )} ${digits.substring(6)}`;
  } else if (digits.length >= 8 && digits.length <= 9) {
    // Landline: +20 X XXX XXXX or +20 XX XXX XXXX
    if (digits.length === 8) {
      return `+20 ${digits.substring(0, 1)} ${digits.substring(
        1,
        4
      )} ${digits.substring(4)}`;
    } else {
      return `+20 ${digits.substring(0, 2)} ${digits.substring(
        2,
        5
      )} ${digits.substring(5)}`;
    }
  }

  return phoneNumber;
};

/**
 * Get placeholder text for Egyptian phone input
 */
export const getEgyptianPhonePlaceholder = (): string => {
  return "01234567890";
};

/**
 * Get help text for Egyptian phone input
 */
export const getEgyptianPhoneHelpText = (): string => {
  return "أدخل رقم هاتفك المصري (مثال: 01234567890)";
};
